import React, { useState } from "react";
import styled, { css } from "styled-components";
import Button from "../Button";
import WhiteBox from "../WhiteBox";
import { colors } from "../../styles";
import { useStepper } from "../../Stepper";
import ServiceProvider from "components/ServiceProvider";
import { useEffect } from "react";
import {
  checkIfOffertIsAlmostExpired,
  getRemainingTime,
} from "utils/CountdownTimerUtils";

const PlanCard = ({ plan, buttonText, defaultTime }) => {
  const { next } = useStepper();
  const [remainingTime, setRemainingTime] = useState(
    getRemainingTime(defaultTime)
  );
  useEffect(() => {
    if (plan.active) {
      const interval = setInterval(() => {
        updateRemainingTime(defaultTime);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [defaultTime]);

  const updateRemainingTime = (countdown) => {
    setRemainingTime(getRemainingTime(countdown));
  };
  const [selectedMethod, setSelectedMethod] = useState(
    (plan.methods && plan.methods[0]) || null
  );

  return (
    <WhiteBox onClick={(e) => e.stopPropagation()}>
      {plan.image && (
        <img src={plan.image} alt="plan" style={{ width: "100%" }} />
      )}
      <Wrapper>
        <Title className="text-center">
          {Array.isArray(plan.name)
            ? plan.name.map((v) => (
                <>
                  {v}
                  <br />
                </>
              ))
            : plan.name}
        </Title>

        <List>
          {plan.description.map((description, i) => (
            <ListItem key={`plan-description-${i}`}>{description}</ListItem>
          ))}
        </List>

        <div>
          <Price
            price={selectedMethod.amount}
            strikedPrice={selectedMethod.oldAmount}
            active={plan.active}
          />

          {plan.methods &&
            plan.methods.map((method) => (
              <SwitchItem
                key={`method-switch-${method.id}`}
                selected={selectedMethod.id === method.id}
                active={plan.active}
                type="button"
                onClick={() => {
                  plan.active && setSelectedMethod(method);
                }}
              >
                {method.frecuency}
              </SwitchItem>
            ))}
        </div>
        <div></div>
        <div
          style={{
            padding: "10px 0",
          }}
        >
          <Button
            onClick={() =>
              next({
                id: plan.id,
                methodId: selectedMethod.id,
              })
            }
            disabled={!plan.active}
          >
            {buttonText}
          </Button>
        </div>

        {/* TODO: Add timer */}

        <CountDownContainer>
          {plan.active ? (
            <>
              Oferta Expira en
              <CountDownWrapper
                almostExpired={checkIfOffertIsAlmostExpired(
                  defaultTime,
                  2,
                  "minutes"
                )}
              >
                <span>{remainingTime.hours}:</span>
                <span>{remainingTime.minutes}:</span>
                <span>{remainingTime.seconds}</span>
              </CountDownWrapper>
            </>
          ) : (
            "Expired"
          )}
        </CountDownContainer>

        <ServiceProvider />
      </Wrapper>
      <CustomBottom />
    </WhiteBox>
  );
};

PlanCard.defaultProps = {
  buttonText: "Reparar Mi Credito",
};

export default PlanCard;

const CountDownWrapper = styled.div`
  color: orange;
  font-weight: bold;
  ${(props) =>
    props.almostExpired &&
    css`
      color: ${colors.red};
    `}
`;

const CountDownContainer = styled.div`
  color: gray;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  text-align: center;

  &,
  * {
    user-select: none;
  }
`;

const CustomBottom = styled.div`
  width: 100%;
  height: 0.5rem;
  background-color: ${colors.blue};
  margin-top: 0.5rem;
`;

const List = styled.ul`
  list-style: none;
`;

const ListItem = styled.li`
  padding-left: 1em;
  text-indent: -1em;
  margin: 5px 0;
  font-size: 1.2em;

  &:before {
    color: ${colors.green};
    content: "✓ ";
  }
`;

const Title = styled.h1`
  padding: 10px 0;
`;

const PriceWrapper = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  ${(props) =>
    !props.active &&
    css`
      color: ${colors.gray};
    `}
`;

const Price = ({ price, strikedPrice, active }) => {
  const str = (price / 100).toString();
  const [dollars, cents] = str.split(".").map((v) => parseInt(v, 10));

  return (
    <PriceWrapper active={active}>
      {strikedPrice && (
        <StrikedPrice>
          <div>${strikedPrice}</div>
        </StrikedPrice>
      )}
      <div style={{ textAlign: "center" }}>
        <PriceDollars>${dollars}</PriceDollars>
        {cents && <PriceCents>.{cents}</PriceCents>}
      </div>
    </PriceWrapper>
  );
};

const PriceDollars = styled.div`
  display: inline-block;
  font-size: 2.3em;
  font-weight: bold;
`;

const PriceCents = styled.div`
  display: inline-block;
  font-weight: bold;
  vertical-align: top;
  padding: 3px 0 0 3px;
`;

const StrikedPrice = styled.div`
  position: relative;
  font-size: 1.5em;
  text-align: center;
  display: flex;
  align-items: center;
  font-weight: bold;
  color: ${colors.gray};
  > div {
    padding: 0.3rem;
  }
  &:after {
    position: absolute;
    content: "";
    left: 0;
    top: 50%;
    right: 0;
    border-top: 2px solid;
    border-color: ${colors.darkGray};
    color: ${colors.gray};
    opacity: 0.7;
    -webkit-transform: rotate(20deg);
    -moz-transform: rotate(20deg);
    -ms-transform: rotate(20deg);
    -o-transform: rotate(20deg);
    transform: rotate(20deg);
  }
`;

const SwitchItem = styled.button`
  display: inline-block;
  border: 0;
  background-color: white;
  text-transform: capitalize;
  cursor: pointer;
  padding: 0.5rem 0.5rem;

  ${(props) =>
    (!props.selected || !props.active) &&
    css`
      color: ${colors.darkGray};
    `}
  ${(props) =>
    props.selected &&
    props.active &&
    css`
      border-bottom: 1px solid ${colors.blue};
    `}
`;
